import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';
import { collectGroupInvariantViolations } from '../../stores/utils/group-row-guards';
import {
  DEFAULT_GROUP_COLOR,
  parseHexColor,
} from '../../components/table/src/utils/group-color';
import type { TableNode } from '../../stores/table-store';
import {
  canonicalNodeNumber,
  isNumberingNodeNumber,
} from '../../stores/utils/node-number';

export interface ImportedGroupMember {
  /** Canonical node number (PSTUDIO-4453). */
  nodeNumber: string;
  order: number;
  /**
   * PSTUDIO-4637: the row itself, when the caller knows it. Node numbers can
   * repeat, so a caller grouping rows already on screen ("Suggest agents")
   * names them by id; CSV import has only the Node Number and leaves this off.
   */
  rowId?: string;
}

export interface ImportedGroupMeta {
  groupId: string;
  groupName: string;
  collapsed: boolean;
  groupColor: string;
  /**
   * PSTUDIO-4122 (S14): the trimmed `Group Color` cell, present only when it
   * was NOT empty and was replaced by the default because the group band cannot
   * paint it. Absent for an empty cell — see `resolveImportedGroupColor`.
   */
  invalidGroupColor?: string;
  members: ImportedGroupMember[];
  /**
   * PSTUDIO-4198: how many rows the file put in this group but
   * `extractCsvGroupMeta` could not read a usable Node Number from (non-numeric,
   * 0, negative or blank). Absent when every row was readable.
   * `planImportedGroups` skips a group that has any, because restoring it would
   * build a group with fewer members than the file lists and report nothing.
   */
  unreadableRows?: number;
}

/**
 * PSTUDIO-4122: what `tableStore.applyImportedGrouping` did, told to the caller.
 *
 * It used to return `void` and raise its own English toast, which left the CSV
 * import unable to distinguish "grouping restored" from "grouping silently
 * dropped" — the whole of Defect A's user-visible symptom. A discriminated union
 * rather than a boolean because the three outcomes need different words, and the
 * partial one needs a count.
 *
 * `version-changed` carries both versions so the report can name them: the graft
 * targets the table by Node Number, and node numbers collide across solutions.
 */
export type ImportGroupingOutcome =
  | { status: 'nothing-to-do' }
  | { status: 'version-changed'; uploadedFor: string; storeIsOn: string }
  | { status: 'applied'; restored: number; skipped: string[] };

/**
 * PSTUDIO-4122: turn a `Group Color` cell into a colour the renderer can use.
 *
 * The exporter writes the group's stored colour verbatim, and a group whose
 * colour the user never picked has none stored — so a faithful export of it
 * carries an EMPTY cell. `draggable-row.tsx` reaches `DEFAULT_GROUP_COLOR`
 * through `rowSettings?.groupColor ?? DEFAULT_GROUP_COLOR`, which fires on
 * `undefined` and NOT on `''`; every style and attribute it derives from the
 * colour is then gated on the value being truthy, so an imported group used to
 * render with no band, no member guide and no `data-group-expanded` at all.
 *
 * Normalising here rather than widening the renderer's fallback keeps the empty
 * value out of the STORE: the colour is written into the synthesised header's
 * settings and persisted, so a later export of the imported group round-trips as
 * a real colour instead of reproducing the empty cell.
 *
 * Whitespace is trimmed for the same reason `Group Id` is (a hand-edited CSV is
 * the expected input): untrimmed, `'   '` is truthy, so the renderer would set
 * `--group-bg-color: '   '` — a band painting nothing, with the text tone
 * computed from an unparsable value.
 *
 * PSTUDIO-4122 (S14): a NON-empty cell is kept only when it is `#` followed by
 * 3, 6 or 8 hex digits. The `#` is required because `draggable-row.tsx` writes
 * the value into the CSS custom property `--group-bg-color` as it is, and
 * `197896` there paints nothing; the digit counts are the ones `parseHexColor`
 * reads, so `getGroupRowTextTone` judges the text tone from the colour actually
 * painted rather than falling back to `dark`. The presets in
 * `GROUP_COLOR_PRESETS` are all `#RRGGBB`. Anything else used to be stored
 * verbatim, so the imported group rendered with no band at all; it now gets the
 * default, and the rejected text is returned as `invalidGroupColor` so the
 * import can say so. An empty cell is defaulted WITHOUT being reported: it is
 * what a faithful export of a never-coloured group carries.
 */
export const resolveImportedGroupColor = (
  cell: string | undefined
): { groupColor: string; invalidGroupColor?: string } => {
  const trimmed = (cell ?? '').trim();
  if (!trimmed) return { groupColor: DEFAULT_GROUP_COLOR };
  if (trimmed.startsWith('#') && parseHexColor(trimmed)) {
    return { groupColor: trimmed };
  }
  return { groupColor: DEFAULT_GROUP_COLOR, invalidGroupColor: trimmed };
};

export interface ImportGroupPlan {
  headers: TableNode[];
  memberUpdates: { rowId: string; groupId: string }[];
  skipped: string[];
}

/**
 * PSTUDIO-3443: Parse CSV records (one per row) and group them by `Group Id`,
 * producing one `ImportedGroupMeta` per unique non-empty Group Id.
 * Members are sorted by `Group Order` ascending.
 * Rows with an empty Group Id or a non-finite Node Number are skipped. A skipped
 * row of a group that still has readable rows is counted in that group's
 * `unreadableRows`; a group with no readable row at all produces no meta.
 */
export const extractCsvGroupMeta = (
  records: Record<string, string>[]
): ImportedGroupMeta[] => {
  const byGroup = new Map<string, ImportedGroupMeta>();
  const unreadableByGroup = new Map<string, number>();

  for (const r of records) {
    const groupId = (r['Group Id'] ?? '').trim();
    if (!groupId) continue;

    const nodeNumber = canonicalNodeNumber(r['Node Number']);
    if (!isNumberingNodeNumber(nodeNumber)) {
      unreadableByGroup.set(groupId, (unreadableByGroup.get(groupId) ?? 0) + 1);
      continue;
    }

    const order = Number(r['Group Order']) || 0;

    const existing = byGroup.get(groupId);
    if (existing) {
      existing.members.push({ nodeNumber, order });
    } else {
      byGroup.set(groupId, {
        groupId,
        groupName: r['Group Name'] ?? '',
        // Group Collapsed is a group-level attribute; take the first member's value (all rows of a group carry the same value in a well-formed export).
        collapsed: r['Group Collapsed'] === '1',
        ...resolveImportedGroupColor(r['Group Color']),
        members: [{ nodeNumber, order }],
      });
    }
  }

  for (const meta of byGroup.values()) {
    meta.members.sort((a, b) => a.order - b.order);
    const unreadable = unreadableByGroup.get(meta.groupId);
    if (unreadable) meta.unreadableRows = unreadable;
  }

  return [...byGroup.values()];
};

/**
 * PSTUDIO-3443: Given parsed group meta and the current post-reload rows,
 * match each member by Node Number (or by `rowId` when given), synthesize a
 * `TableNode` group header (id = Group Id verbatim), and produce the
 * member-update list.
 *
 * Groups with no matched members, any unmatched member, any row the file named
 * but `extractCsvGroupMeta` could not read (`unreadableRows`), or an invalid
 * invariant probe are pushed to `skipped` and dropped. A group of ONE member is
 * a valid group (PSTUDIO-4198); what it must not do is stand in for a larger
 * group that lost rows on the way in.
 */
export const planImportedGroups = (
  groupMeta: ImportedGroupMeta[],
  currentRows: TableNode[]
): ImportGroupPlan => {
  const byNumber = new Map<string, TableNode>();
  const byId = new Map<string, TableNode>();
  for (const row of currentRows) {
    byId.set(row.id, row);
    if (row.nodeNumber) byNumber.set(row.nodeNumber, row);
  }

  const headers: TableNode[] = [];
  const memberUpdates: { rowId: string; groupId: string }[] = [];
  const skipped: string[] = [];

  for (const meta of groupMeta) {
    const matched = meta.members.map((m) =>
      m.rowId !== undefined ? byId.get(m.rowId) : byNumber.get(m.nodeNumber)
    );

    if (
      matched.length < 1 ||
      meta.unreadableRows ||
      matched.some((row) => !row)
    ) {
      skipped.push(meta.groupId);
      continue;
    }

    const memberIds = (matched as TableNode[]).map((row) => row.id);

    const header = {
      id: meta.groupId,
      nodeType: Tenancy_Node_Type_Enum_Ps_Enum.G,
      nodeNumber: null,
      settings: {
        subRows: memberIds,
        groupName: meta.groupName,
        groupExpanded: !meta.collapsed,
        groupColor: meta.groupColor,
      },
    } as unknown as TableNode;

    // Build a probe with the header + members-with-groupId set so
    // collectGroupInvariantViolations can check bidirectional subRows↔groupId.
    const probe = [
      header,
      ...(matched as TableNode[]).map((row) => ({
        ...row,
        settings: { ...row.settings, groupId: meta.groupId },
      })),
    ];

    if (collectGroupInvariantViolations(probe).length > 0) {
      skipped.push(meta.groupId);
      continue;
    }

    headers.push(header);
    for (const rowId of memberIds) {
      memberUpdates.push({ rowId, groupId: meta.groupId });
    }
  }

  return { headers, memberUpdates, skipped };
};
